import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import ContentCard from "../../components/ContentCard";
import { getCompanyId } from "../../utils/api";
import { apiRequest } from "../../utils/apiRequest";
import { buildRfiRequestBody, statusMap } from "../../utils/requestBodies.js";
import { showToast } from "../../utils/toastOptions";
import SearchableSelect from "./SearchableSelect";
import { safeDisplay } from "./sharedDropdownOptions";

const RequiredLabel = ({ label }) => (
  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
    {label} <span style={{ color: "red" }}>*</span>
  </Typography>
);

RequiredLabel.propTypes = {
  label: PropTypes.string.isRequired,
};

const CreateRfiForm = forwardRef(
  ({ formData, setFormData, rfiDropdownOptions, selectedProject, setRefreshRfIs }, ref) => {
    const handleChange = (event) => {
      const { name, value, type, checked } = event.target;
      const newValue = type === "checkbox" ? checked : value;

      setFormData((prev) => ({
        ...prev,
        rfi: {
          ...prev.rfi,
          [name]: newValue,
        },
      }));
    };

    const parseNameAndCompany = (name) => {
      const trimmedName = name.trim();
      const firstOpenParen = trimmedName.indexOf("(");
      const lastCloseParen = trimmedName.lastIndexOf(")");

      if (firstOpenParen !== -1 && lastCloseParen > firstOpenParen) {
        const personName = trimmedName.substring(0, firstOpenParen).trim();
        const company = trimmedName.substring(firstOpenParen + 1, lastCloseParen).trim();
        return {
          personName,
          company,
        };
      }
      return {
        personName: trimmedName,
        company: null,
      };
    };

    const transformReceivedFromOptions = (options) => {
      return (options || []).map((option) => {
        const { personName, company } = parseNameAndCompany(option.name);
        return {
          ...option,
          personName,
          company,
        };
      });
    };

    const transformResponsibleContractorOptions = (options) => {
      return (options || []).map((option) => {
        return {
          ...option,
          personName: option.name,
          company: option.name,
        };
      });
    };

    const [localRfi, setLocalRfi] = useState(formData.rfi || {});

    const receivedFromOptionsTransformed = useMemo(() => {
      return transformReceivedFromOptions(rfiDropdownOptions.receivedFrom || []);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rfiDropdownOptions.receivedFrom]);

    const responsibleContractorOptionsTransformed = useMemo(() => {
      return transformResponsibleContractorOptions(rfiDropdownOptions.responsibleContractor || []);
    }, [rfiDropdownOptions.responsibleContractor]);

    const receivedFromOptions = useMemo(() => {
      const allOptions = receivedFromOptionsTransformed;
      const selectedContractor = formData.rfi?.responsibleContractor;

      if (!selectedContractor) {
        return allOptions;
      }

      const selectedContractorOption = responsibleContractorOptionsTransformed.find(
        (opt) => opt.id === selectedContractor
      );

      if (!selectedContractorOption?.company) {
        return allOptions;
      }

      const selectedCompany = selectedContractorOption.company
        .trim()
        .toLowerCase()
        .replaceAll(/\s+/g, " ");
      return allOptions.filter((option) => {
        if (!option.company) return false;
        const optionCompany = option.company.trim().toLowerCase().replaceAll(/\s+/g, " ");
        return optionCompany === selectedCompany;
      });
    }, [
      receivedFromOptionsTransformed,
      responsibleContractorOptionsTransformed,
      formData.rfi?.responsibleContractor,
    ]);

    const responsibleContractorOptions = useMemo(() => {
      const allOptions = responsibleContractorOptionsTransformed;
      const selectedReceivedFrom = formData.rfi?.receivedFrom;

      if (!selectedReceivedFrom) {
        return allOptions;
      }

      const selectedReceivedFromOption = receivedFromOptionsTransformed.find(
        (opt) => opt.id === selectedReceivedFrom
      );

      if (!selectedReceivedFromOption?.company) {
        return allOptions;
      }

      const selectedCompany = selectedReceivedFromOption.company
        .trim()
        .toLowerCase()
        .replaceAll(/\s+/g, " ");
      return allOptions.filter((option) => {
        if (!option.company) return false;
        const optionCompany = option.company.trim().toLowerCase().replaceAll(/\s+/g, " ");
        return optionCompany === selectedCompany;
      });
    }, [
      responsibleContractorOptionsTransformed,
      receivedFromOptionsTransformed,
      formData.rfi?.receivedFrom,
    ]);

    useEffect(() => {
      const selectedContractor = formData.rfi?.responsibleContractor;
      const selectedReceivedFrom = formData.rfi?.receivedFrom;

      if (selectedContractor && selectedReceivedFrom) {
        const contractorOption = responsibleContractorOptionsTransformed.find(
          (opt) => opt.id === selectedContractor
        );
        const receivedFromOption = receivedFromOptionsTransformed.find(
          (opt) => opt.id === selectedReceivedFrom
        );

        if (contractorOption?.company && receivedFromOption?.company) {
          const contractorCompany = contractorOption.company.trim().toLowerCase();
          const receivedFromCompany = receivedFromOption.company.trim().toLowerCase();

          if (contractorCompany !== receivedFromCompany) {
            setFormData((prev) => ({
              ...prev,
              rfi: { ...prev.rfi, receivedFrom: "" },
            }));
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      formData.rfi?.responsibleContractor,
      responsibleContractorOptionsTransformed,
      receivedFromOptionsTransformed,
    ]);

    useEffect(() => {
      const selectedContractor = formData.rfi?.responsibleContractor;
      const selectedReceivedFrom = formData.rfi?.receivedFrom;

      if (selectedContractor && selectedReceivedFrom) {
        const contractorOption = responsibleContractorOptionsTransformed.find(
          (opt) => opt.id === selectedContractor
        );
        const receivedFromOption = receivedFromOptionsTransformed.find(
          (opt) => opt.id === selectedReceivedFrom
        );

        if (contractorOption?.company && receivedFromOption?.company) {
          const contractorCompany = contractorOption.company.trim().toLowerCase();
          const receivedFromCompany = receivedFromOption.company.trim().toLowerCase();

          if (contractorCompany !== receivedFromCompany) {
            setFormData((prev) => ({
              ...prev,
              rfi: { ...prev.rfi, responsibleContractor: "" },
            }));
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      formData.rfi?.receivedFrom,
      responsibleContractorOptionsTransformed,
      receivedFromOptionsTransformed,
    ]);

    useEffect(() => {
      setLocalRfi(formData.rfi || {});
    }, [formData.rfi]);

    function useDebounce(value, delay = 200) {
      const [debouncedValue, setDebouncedValue] = useState(value);

      useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
      }, [value, delay]);

      return debouncedValue;
    }

    const debouncedRfi = useDebounce(localRfi, 200);

    useEffect(() => {
      setFormData((prev) => ({ ...prev, rfi: debouncedRfi }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedRfi]);

    // Unique handle for assignees field
    const buildAssigneeFromId = (id, prevAssignees) => {
      const existing = prevAssignees.find((a) => a.id === id);
      if (existing) return existing;
      const found = rfiDropdownOptions.assignees.find((u) => u.id === id);
      return { id, name: found?.name || String(id), required: false };
    };

    const handleAssigneeChange = (event) => {
      const selectedIds = event.target.value || [];
      setFormData((prev) => {
        const prevAssignees = prev.rfi?.assignees || [];
        const prevRequiredIds = prev.rfi?.requiredAssigneeIds || [];

        const newAssignees = selectedIds.map((id) => buildAssigneeFromId(id, prevAssignees));

        const newRequiredIds = prevRequiredIds.filter((id) => selectedIds.includes(id));

        return {
          ...prev,
          rfi: {
            ...prev.rfi,
            assignees: newAssignees,
            requiredAssigneeIds: newRequiredIds,
          },
        };
      });
    };

    const toggleRequired = (e, id) => {
      e.stopPropagation();
      e.preventDefault();

      setFormData((prev) => {
        const updatedAssignees = (prev.rfi?.assignees || []).map((a) =>
          a.id === id ? { ...a, required: !a.required } : a
        );

        let updatedRequiredIds = prev.rfi?.requiredAssigneeIds || [];
        if (updatedRequiredIds.includes(id)) {
          updatedRequiredIds = updatedRequiredIds.filter((rid) => rid !== id);
        } else {
          updatedRequiredIds.push(id);
        }

        return {
          ...prev,
          rfi: {
            ...prev.rfi,
            assignees: updatedAssignees,
            requiredAssigneeIds: updatedRequiredIds,
          },
        };
      });
    };

    const removeAssignee = (e, id) => {
      e.stopPropagation();
      e.preventDefault();

      setFormData((prev) => ({
        ...prev,
        rfi: {
          ...prev.rfi,
          assignees: (prev.rfi?.assignees || []).filter((a) => a.id !== id),
        },
      }));
    };

    useEffect(() => {
      if (!rfiDropdownOptions.assignees || rfiDropdownOptions.assignees.length === 0) return;

      const raw = formData.rfi?.assignees;

      if (!raw) {
        setFormData((prev) => ({
          ...prev,
          rfi: {
            ...prev.rfi,
            assignees: [],
            requiredAssigneeIds: [],
          },
        }));
        return;
      }

      if (Array.isArray(raw) && raw.length && typeof raw[0] !== "object") {
        const converted = raw.map((id) => {
          const found = rfiDropdownOptions.assignees.find((u) => u.id === id);
          return { id, name: found?.name || String(id), required: false };
        });
        setFormData((prev) => ({
          ...prev,
          rfi: {
            ...prev.rfi,
            assignees: converted,
            requiredAssigneeIds: [],
          },
        }));
      } else if (Array.isArray(raw) && raw.length && typeof raw[0] === "object") {
        setFormData((prev) => ({
          ...prev,
          rfi: {
            ...prev.rfi,
            requiredAssigneeIds: prev.rfi?.requiredAssigneeIds || [],
          },
        }));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rfiDropdownOptions.assignees]);

    const handleSubmit = useCallback(async () => {
      if (!selectedProject?.id) {
        showToast("error", "Please select a project before submitting.");
        return;
      }

      try {
        const requestBody = buildRfiRequestBody(selectedProject.id, formData, statusMap);

        const companyId = getCompanyId(selectedProject);
        if (!companyId) {
          showToast("error", "Project missing Company ID");
          return;
        }
        await apiRequest(
          `${process.env.REACT_APP_API_BASE_URL}/change-management/companies/${companyId}/projects/${selectedProject.id}/rfis/`,
          { method: "POST", body: requestBody }
        );

        setRefreshRfIs((prev) => !prev);
        showToast("success", "RFI created successfully!");
        setFormData({});
      } catch (error) {
        showToast("error", error.message || "Failed to create RFI");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProject, formData, setRefreshRfIs]);

    useImperativeHandle(
      ref,
      () => ({
        handleSubmit,
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [selectedProject, formData]
    );

    const selectStyle = {
      minWidth: 354,
      maxWidth: 354,
      height: 36,
      "& .MuiSelect-select": {
        padding: "6px 12px",
      },
    };

    const inputStyle = {
      width: 354,
      "& .MuiInputBase-root": {
        height: 36,
      },
      "& .MuiInputBase-input": {
        padding: "0 12px",
      },
    };

    return (
      <ContentCard elevation={0} p={3}>
        {/* Request section */}
        <Typography variant="h6" gutterBottom>
          Request
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3} mb={2}>
          <Grid item xs={12}>
            <RequiredLabel label="Subject" />
            <TextField
              required={true}
              size="small"
              sx={inputStyle}
              id="subject"
              name="subject"
              fullWidth
              autoComplete="off"
              value={localRfi.subject || ""}
              placeholder="Enter Subject"
              InputLabelProps={{ shrink: false }}
              onChange={(e) => setLocalRfi((prev) => ({ ...prev, subject: e.target.value }))}
            />
          </Grid>
        </Grid>

        <Grid container spacing={3} mb={2}>
          <Grid item xs={12}>
            <RequiredLabel label="Question" />
            <TextField
              required={true}
              size="small"
              sx={{
                width: 620,
              }}
              id="question"
              name="question"
              multiline
              rows={4}
              fullWidth
              value={localRfi.question || ""}
              placeholder="Question"
              InputLabelProps={{ shrink: false }}
              onChange={(e) => setLocalRfi((prev) => ({ ...prev, question: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} mt={3.3}>
            <Box
              sx={{
                border: "1px dashed #ccc",
                p: 2,
                textAlign: "center",
                borderRadius: 1,
                height: 76,
                width: 619,
              }}
            >
              <Typography variant="body2" color="textSecondary">
                Attach Files or Drag & Drop
              </Typography>
              <Button variant="contained" sx={{ mt: 1 }}>
                Attach Files
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* info section */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Information
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3} mb={2}>
          <Grid item xs={12} sm={6}>
            <RequiredLabel label="Number" />
            <TextField
              required={true}
              size="small"
              sx={inputStyle}
              id="number"
              name="number"
              fullWidth
              value={localRfi.number || ""}
              placeholder="Enter Number"
              InputLabelProps={{ shrink: false }}
              onChange={(e) => setLocalRfi((prev) => ({ ...prev, number: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <RequiredLabel label="Due Date" />
            <DatePicker
              value={formData.rfi?.dueDate ? dayjs(formData.rfi.dueDate) : null}
              onChange={(newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  rfi: {
                    ...prev.rfi,
                    dueDate: newValue ? newValue.format("YYYY-MM-DD") : "",
                  },
                }));
              }}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  placeholder: "yyyy-mm-dd",
                  sx: inputStyle,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <RequiredLabel label="RFI Manager" />
            <FormControl sx={selectStyle} size="small" fullWidth>
              <SearchableSelect
                required={true}
                name="rfiManager"
                value={formData.rfi?.rfiManager || ""}
                displayEmpty
                onChange={handleChange}
                renderValue={(value) =>
                  value === "" ? (
                    <span style={{ color: "#9e9e9e" }}>Select a Person</span>
                  ) : (
                    safeDisplay(
                      rfiDropdownOptions.rfiManager?.find((item) => item.id === value)?.name ??
                        value
                    )
                  )
                }
              >
                <MenuItem value="" disabled={true} color="text.secondary">
                  <em>Select a Person</em>
                </MenuItem>
                {rfiDropdownOptions.rfiManager.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {safeDisplay(item.name)}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} mt={3.7}>
            <Typography color="text.secondary">Status: --</Typography>
          </Grid>
        </Grid>

        {/* Received From */}

        <Grid container spacing={3} mb={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Received From
            </Typography>
            <FormControl sx={selectStyle} size="small" fullWidth>
              <SearchableSelect
                name="receivedFrom"
                value={formData.rfi?.receivedFrom || ""}
                displayEmpty
                onChange={handleChange}
                renderValue={(value) => {
                  if (value === "")
                    return <span style={{ color: "#9e9e9e" }}>Select a Person</span>;
                  const option = receivedFromOptions.find((item) => item.id === value);
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        pr: 1,
                        gap: 1,
                      }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {option?.personName || value}
                      </span>
                      <IconButton
                        size="small"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setFormData((prev) => ({
                            ...prev,
                            rfi: { ...prev.rfi, receivedFrom: "" },
                          }));
                        }}
                        sx={{
                          p: 0.5,
                          color: "text.secondary",
                          flexShrink: 0,
                          "&:hover": { color: "error.main", bgcolor: "transparent" },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                }}
              >
                <MenuItem value="" disabled={true} color="text.secondary">
                  <em>Select a Person</em>
                </MenuItem>
                {receivedFromOptions.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.personName}
                      </Typography>
                      {item.company != null && (
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", display: "block" }}
                        >
                          {item.company}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <RequiredLabel label="Assignee" />
            <FormControl sx={selectStyle} size="small" fullWidth>
              <SearchableSelect
                name="assignees"
                multiple
                value={formData.rfi?.assignees?.map((a) => a.id) || []}
                displayEmpty
                onChange={handleAssigneeChange}
                renderValue={() => {
                  const assignees = formData.rfi?.assignees || [];
                  if (!assignees || assignees.length === 0) {
                    return <span style={{ color: "#9e9e9e" }}>Select a Person</span>;
                  }
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        overflowX: "auto",
                        whiteSpace: "nowrap",
                        gap: 0.5,
                        padding: "2px 0",
                        maxHeight: 36,
                      }}
                    >
                      {assignees.map((a) => (
                        <Box
                          key={a.id}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            borderRadius: 1,
                            border: "1px solid rgba(0,0,0,0.12)",
                            px: 0.5,
                            py: 0.25,
                            flexShrink: 0,
                            background: "#f5f5f5",
                          }}
                        >
                          <Tooltip title="Make Assignee Required" arrow>
                            <Checkbox //add a handle assignee required
                              checked={formData.rfi?.requiredAssigneeIds?.includes(a.id)}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleRequired(e, a.id);
                              }}
                              size="small"
                              sx={{ p: 0.2, mr: 0.2 }}
                            />
                          </Tooltip>
                          <Typography>{safeDisplay(a.name)}</Typography>
                          <IconButton
                            size="small"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeAssignee(e, a.id);
                            }}
                            sx={{ p: "2px", ml: 0.5 }}
                            aria-label={`Remove ${safeDisplay(a.name)}`}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  );
                }}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 250 } },
                }}
              >
                <MenuItem value="" disabled={true} color="text.secondary">
                  <em>Select a Person</em>
                </MenuItem>
                {rfiDropdownOptions.assignees.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    <Checkbox
                      checked={(formData.rfi?.assignees || []).some((a) => a.id === item.id)}
                      size="small"
                      sx={{ p: 0.5, mr: 1 }}
                    />
                    {safeDisplay(item.name)}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Distribution List
            </Typography>
            <FormControl sx={selectStyle} size="small" fullWidth>
              <SearchableSelect
                multiple
                name="distributionList"
                value={formData.rfi?.distributionList || []}
                displayEmpty
                onChange={handleChange}
              >
                <MenuItem value="" disabled={true} color="text.secondary">
                  <em>Select People</em>
                </MenuItem>
                {rfiDropdownOptions.distributionList.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} mt={3.7}>
            <Typography color="text.secondary">Ball In Court: --</Typography>
          </Grid>
        </Grid>

        {/* Responsible Contractor */}

        <Grid container spacing={3} mb={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Responsible Contractor
            </Typography>
            <FormControl sx={selectStyle} size="small" fullWidth>
              <SearchableSelect
                name="responsibleContractor"
                value={formData.rfi?.responsibleContractor || ""}
                displayEmpty
                onChange={handleChange}
                renderValue={(value) => {
                  if (value === "")
                    return <span style={{ color: "#9e9e9e" }}>Select a Vendor</span>;
                  const option = responsibleContractorOptions.find((item) => item.id === value);
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        pr: 1,
                        gap: 1,
                      }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {option?.company || option?.personName || value}
                      </span>
                      <IconButton
                        size="small"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setFormData((prev) => ({
                            ...prev,
                            rfi: { ...prev.rfi, responsibleContractor: "" },
                          }));
                        }}
                        sx={{
                          p: 0.5,
                          color: "text.secondary",
                          flexShrink: 0,
                          "&:hover": { color: "error.main", bgcolor: "transparent" },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                }}
              >
                <MenuItem value="" disabled={true} color="text.secondary">
                  <em>Select a Vendor</em>
                </MenuItem>
                {responsibleContractorOptions.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {safeDisplay(item.name)}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Specification
            </Typography>
            <FormControl sx={selectStyle} size="small" fullWidth>
              <SearchableSelect
                name="specification"
                value={formData.rfi?.specification || ""}
                displayEmpty
                onChange={handleChange}
                renderValue={(value) =>
                  value === "" ? (
                    <span style={{ color: "#9e9e9e" }}>Select Specification</span>
                  ) : (
                    safeDisplay(
                      rfiDropdownOptions.specification?.find((item) => item.id === value)?.name ??
                        value
                    )
                  )
                }
              >
                <MenuItem value="" disabled={true} color="text.secondary">
                  <em>Select Specification</em>
                </MenuItem>
                {rfiDropdownOptions.specification.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {safeDisplay(item.name)}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Location
            </Typography>
            <FormControl sx={selectStyle} size="small" fullWidth>
              <SearchableSelect
                name="location"
                value={formData.rfi?.location || ""}
                displayEmpty
                onChange={handleChange}
                renderValue={(value) =>
                  value === "" ? (
                    <span style={{ color: "#9e9e9e" }}>Select Location</span>
                  ) : (
                    safeDisplay(
                      rfiDropdownOptions.location?.find((item) => item.id === value)?.name ?? value
                    )
                  )
                }
              >
                <MenuItem value="" disabled={true} color="text.secondary">
                  <em>Select Location</em>
                </MenuItem>
                {rfiDropdownOptions.location.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {safeDisplay(item.name)}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} mt={3.7}>
            <Typography color="text.secondary">Created By: --</Typography>
          </Grid>
        </Grid>

        {/* RFI Stage */}

        <Grid container spacing={3} mb={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              RFI Stage
            </Typography>
            <FormControl sx={selectStyle} size="small" fullWidth>
              <SearchableSelect
                name="rfiStage"
                value={formData.rfi?.rfiStage || ""}
                displayEmpty
                onChange={handleChange}
                renderValue={(value) =>
                  value === "" ? (
                    <span style={{ color: "#9e9e9e" }}>Select Stage</span>
                  ) : (
                    safeDisplay(
                      rfiDropdownOptions.rfiStage?.find((item) => item.id === value)?.name ?? value
                    )
                  )
                }
              >
                <MenuItem value="" disabled={true} color="text.secondary">
                  <em>Select Stage</em>
                </MenuItem>
                {rfiDropdownOptions.rfiStage.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {safeDisplay(item.name)}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Drawing Number
            </Typography>
            <TextField
              size="small"
              sx={inputStyle}
              id="drawingNumber"
              name="drawingNumber"
              fullWidth
              value={localRfi.drawingNumber || ""}
              placeholder="Enter Drawing Number"
              InputLabelProps={{ shrink: false }}
              onChange={(e) => setLocalRfi((prev) => ({ ...prev, drawingNumber: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl sx={selectStyle} size="small" fullWidth>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Cost Code
              </Typography>
              <SearchableSelect
                name="costCode"
                value={formData.rfi?.costCode || ""}
                displayEmpty
                onChange={handleChange}
                renderValue={(value) =>
                  value === "" ? (
                    <span style={{ color: "#9e9e9e" }}>Select Cost Code</span>
                  ) : (
                    safeDisplay(
                      rfiDropdownOptions.costCode?.find((item) => item.id === value)?.name ?? value
                    )
                  )
                }
              >
                <MenuItem value="" disabled={true} color="text.secondary">
                  <em>Select Cost Code</em>
                </MenuItem>
                {rfiDropdownOptions.costCode.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {safeDisplay(item.name)}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} mt={3.7}>
            <Typography color="text.secondary">Date Initiated: --</Typography>
          </Grid>
        </Grid>

        {/* Schedule Impact */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Schedule Impact
            </Typography>
            <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
              <FormControl
                sx={{
                  height: 36,
                  "& .MuiSelect-select": {
                    padding: "6px 12px",
                  },
                  width: formData.rfi?.scheduleImpact === "yes_known" ? 170 : 354,
                }}
                size="small"
                fullWidth
              >
                <SearchableSelect
                  name="scheduleImpact"
                  value={formData.rfi?.scheduleImpact || ""}
                  displayEmpty
                  onChange={handleChange}
                  renderValue={(value) =>
                    value === "" ? (
                      <span style={{ color: "#9e9e9e" }}>Select Impact</span>
                    ) : (
                      rfiDropdownOptions.scheduleImpact.find((item) => item.value === value)
                        ?.name || value
                    )
                  }
                >
                  <MenuItem value="" disabled={true} color="text.secondary">
                    <em>Select Impact</em>
                  </MenuItem>
                  {rfiDropdownOptions.scheduleImpact.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {safeDisplay(item.name)}
                    </MenuItem>
                  ))}
                </SearchableSelect>
              </FormControl>

              {formData.rfi?.scheduleImpact === "yes_known" && (
                <TextField
                  sx={{
                    "& .MuiInputBase-root": { height: 36 },
                    "& .MuiInputBase-input": { padding: "0 12px" },
                    width: 170,
                    transition: "width 0.3s ease",
                  }}
                  name="scheduleImpactDetails"
                  value={formData.rfi?.scheduleImpactDetails || ""}
                  placeholder="Days*"
                  onChange={handleChange}
                  required={true}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Cost Impact
            </Typography>
            <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
              <FormControl
                sx={{
                  height: 36,
                  "& .MuiSelect-select": {
                    padding: "6px 12px",
                  },
                  width: formData.rfi?.costImpact === "yes_known" ? 170 : 354,
                }}
                size="small"
                fullWidth
              >
                <SearchableSelect
                  name="costImpact"
                  value={formData.rfi?.costImpact || ""}
                  displayEmpty
                  onChange={handleChange}
                  renderValue={(value) =>
                    value === "" ? (
                      <span style={{ color: "#9e9e9e" }}>Select Impact</span>
                    ) : (
                      safeDisplay(
                        rfiDropdownOptions.costImpact?.find((item) => item.value === value)?.name ??
                          value
                      )
                    )
                  }
                >
                  <MenuItem value="" disabled={true} color="text.secondary">
                    <em>Select Impact</em>
                  </MenuItem>
                  {rfiDropdownOptions.costImpact.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {safeDisplay(item.name)}
                    </MenuItem>
                  ))}
                </SearchableSelect>
              </FormControl>

              {formData.rfi?.costImpact === "yes_known" && (
                <TextField
                  sx={{
                    "& .MuiInputBase-root": {
                      height: 36,
                    },
                    "& .MuiInputBase-input": {
                      padding: "0 12px",
                    },
                    width: 170,
                    transition: "flex 0.3s ease",
                  }}
                  name="costImpactDetails"
                  value={formData.rfi?.costImpactDetails || ""}
                  placeholder="$ 0.00*"
                  onChange={handleChange}
                  required={true}
                />
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Reference
            </Typography>
            <TextField
              size="small"
              sx={inputStyle}
              id="reference"
              name="reference"
              fullWidth
              value={localRfi.reference || ""}
              placeholder="Enter Reference"
              InputLabelProps={{ shrink: false }}
              onChange={(e) => setLocalRfi((prev) => ({ ...prev, reference: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} mt={2.8}>
            <FormControlLabel
              control={
                <Checkbox
                  name="private"
                  checked={formData.rfi?.private || false}
                  onChange={handleChange}
                />
              }
              label="Private"
            />
          </Grid>
        </Grid>
      </ContentCard>
    );
  }
);

CreateRfiForm.propTypes = {
  formData: PropTypes.shape({
    rfi: PropTypes.object,
  }).isRequired,
  setFormData: PropTypes.func.isRequired,
  rfiDropdownOptions: PropTypes.shape({
    receivedFrom: PropTypes.arrayOf(PropTypes.object),
    responsibleContractor: PropTypes.arrayOf(PropTypes.object),
    scheduleImpact: PropTypes.arrayOf(PropTypes.object),
    costImpact: PropTypes.arrayOf(PropTypes.object),
    assignees: PropTypes.arrayOf(PropTypes.object),
    rfiManager: PropTypes.arrayOf(PropTypes.object),
    distributionList: PropTypes.arrayOf(PropTypes.object),
    specification: PropTypes.arrayOf(PropTypes.object),
    location: PropTypes.arrayOf(PropTypes.object),
    rfiStage: PropTypes.arrayOf(PropTypes.object),
    costCode: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  selectedProject: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    company: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    company_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  setRefreshRfIs: PropTypes.func.isRequired,
};

export default CreateRfiForm;
